import { useCallback, useEffect, useRef, useState } from "react"

import { logger } from "@/lib/logger"
import type { OverageSettingsResponse } from "@/types_api/backend/billing/overage_settings_response"
import type { OverageSettingsUpdate } from "@/types_api/backend/billing/overage_settings_update"

import {
  assertOverageSettingsScope,
  createOverageSettingsDraft,
  validateOverageSettingsDraft,
  type OverageSettingsDraft,
} from "../utils/overageSettings"

const log = logger("OverageSettingsEditor")

export type OverageSettingsFailure =
  | "conflict"
  | "not-configured"
  | "rejected"
  | "uncertain"

export interface OverageSettingsPort {
  read: (signal: AbortSignal) => Promise<OverageSettingsResponse>
  update: (
    request: OverageSettingsUpdate,
    signal: AbortSignal,
  ) => Promise<OverageSettingsResponse>
  invalidate: () => Promise<void>
  classifyFailure: (error: unknown) => OverageSettingsFailure
}

type Feedback =
  | "load-error"
  | "conflict"
  | "not-configured"
  | "rejected"
  | "uncertain"
  | "saved"
  | "superseded"
  | null

export function useOverageSettingsEditor(
  organizationId: string,
  port: OverageSettingsPort,
) {
  const [data, setData] = useState<OverageSettingsResponse | null>(null)
  const [draft, setDraft] = useState<OverageSettingsDraft | null>(null)
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [isReading, setIsReading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [pendingRequest, setPendingRequest] =
    useState<OverageSettingsUpdate | null>(null)
  const generation = useRef(0)
  const inFlight = useRef(false)
  const abort = useRef<AbortController | null>(null)

  const load = useCallback(async () => {
    if (inFlight.current) {
      return
    }
    const requestGeneration = generation.current
    const controller = new AbortController()
    abort.current = controller
    inFlight.current = true
    setIsReading(true)
    try {
      const response = await port.read(controller.signal)
      if (
        controller.signal.aborted ||
        generation.current !== requestGeneration
      ) {
        return
      }
      assertOverageSettingsScope(response, organizationId)
      setData(response)
      setDraft(
        response.controls
          ? createOverageSettingsDraft(response.controls)
          : null,
      )
      setFeedback(null)
      setAttempted(false)
      setPendingRequest(null)
    } catch {
      if (
        !controller.signal.aborted &&
        generation.current === requestGeneration
      ) {
        setFeedback("load-error")
      }
    } finally {
      if (generation.current === requestGeneration) {
        inFlight.current = false
        setIsReading(false)
      }
    }
  }, [organizationId, port])

  useEffect(() => {
    generation.current += 1
    setData(null)
    setDraft(null)
    setFeedback(null)
    setPendingRequest(null)
    setAttempted(false)
    setIsSaving(false)
    inFlight.current = false
    void load()
    return () => {
      generation.current += 1
      abort.current?.abort()
    }
  }, [load])

  const validation =
    draft && data?.controls
      ? validateOverageSettingsDraft(draft, data.controls.currency)
      : { limitMinor: null, amountError: null }
  const hasChanges =
    draft !== null &&
    data?.controls != null &&
    (draft.overagesEnabled !== data.controls.overages_enabled ||
      draft.limitState !== data.controls.limit_state ||
      (validation.amountError === null &&
        validation.limitMinor !== data.controls.limit_minor) ||
      validation.amountError !== null)
  const blocked =
    isReading ||
    isSaving ||
    pendingRequest !== null ||
    feedback === "conflict" ||
    feedback === "not-configured" ||
    feedback === "load-error"

  const save = useCallback(async () => {
    if (inFlight.current || !data?.controls || !draft) {
      return
    }
    if (!pendingRequest && (blocked || !hasChanges)) {
      return
    }
    setAttempted(true)
    const result = validateOverageSettingsDraft(draft, data.controls.currency)
    if (result.amountError) {
      return
    }
    const request = pendingRequest ?? {
      request_id: crypto.randomUUID(),
      expected_revision_id: data.controls.revision_id,
      overages_enabled: draft.overagesEnabled,
      limit_state: draft.limitState,
      limit_minor: result.limitMinor,
    }
    const requestGeneration = generation.current
    const controller = new AbortController()
    abort.current = controller
    inFlight.current = true
    setIsSaving(true)
    try {
      const response = await port.update(request, controller.signal)
      if (
        controller.signal.aborted ||
        generation.current !== requestGeneration
      ) {
        return
      }
      assertOverageSettingsScope(response, organizationId)
      if (
        !response.configured ||
        response.accepted_revision_id !==
          `member-settings:${request.request_id}`
      ) {
        throw new Error("The accepted settings request could not be verified.")
      }
      setData(response)
      setDraft(
        response.controls
          ? createOverageSettingsDraft(response.controls)
          : null,
      )
      setPendingRequest(null)
      setFeedback(
        response.controls?.revision_id === response.accepted_revision_id
          ? "saved"
          : "superseded",
      )
      setAttempted(false)
    } catch (error: unknown) {
      if (
        !controller.signal.aborted &&
        generation.current === requestGeneration
      ) {
        const failure = port.classifyFailure(error)
        setPendingRequest(failure === "uncertain" ? request : null)
        setFeedback(failure)
      }
    } finally {
      try {
        await port.invalidate()
      } catch (error: unknown) {
        log.warn("Could not refresh overage settings caches", {
          organizationId,
          error,
        })
      }
      if (generation.current === requestGeneration) {
        inFlight.current = false
        setIsSaving(false)
      }
    }
  }, [blocked, data, draft, hasChanges, organizationId, pendingRequest, port])

  const changeDraft = useCallback(
    (next: OverageSettingsDraft) => {
      if (!blocked) {
        setDraft(next)
        setFeedback(null)
      }
    },
    [blocked],
  )

  return {
    data,
    draft,
    feedback,
    isReading,
    isSaving,
    pendingRequest,
    blocked,
    hasChanges,
    amountError: attempted ? validation.amountError : null,
    validationIssues: validation.amountError ? [validation.amountError] : [],
    load,
    save,
    changeDraft,
    revealValidation: () => setAttempted(true),
  }
}
